import { useRef, useState } from 'react';

import {
  AlertDialog,
  AlertDialogBody,
  AlertDialogContent,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogOverlay,
  Box,
  Button,
  Center,
  Flex,
  IconButton,
  Spinner,
  Switch,
  Tag,
  Text,
} from '@chakra-ui/react';

import { AddCircleOutline, DeleteOutline, useToast } from '@aslanpay/portal-common-lib';

import {
  ManagedRecognitionValue,
  RecognitionValueFormState,
  RecognitionValuesManagerProps,
} from './types';
import RecognitionValueModal from './RecognitionValueModal';

function RecognitionValuesManager({
  description,
  values,
  availableCards,
  isLoading,
  showEnabledToggle,
  isSaving,
  onCreate,
  onUpdate,
  onDelete,
  onToggleEnabled,
  onUploadCard,
}: RecognitionValuesManagerProps) {
  const toast = useToast();

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingValue, setEditingValue] = useState<ManagedRecognitionValue | null>(null);
  const [valuePendingDeletion, setValuePendingDeletion] = useState<ManagedRecognitionValue | null>(
    null
  );
  const [isDeleting, setIsDeleting] = useState(false);
  const cancelDeleteRef = useRef<HTMLButtonElement>(null);

  const editableValues = values.filter((value) => value.type !== 'REASON');

  const openCreateModal = () => {
    setEditingValue(null);
    setIsModalOpen(true);
  };

  const openEditModal = (value: ManagedRecognitionValue) => {
    setEditingValue(value);
    setIsModalOpen(true);
  };

  const handleSubmit = async (form: RecognitionValueFormState) => {
    try {
      if (editingValue) {
        await onUpdate(editingValue.id, form);
      } else {
        await onCreate(form);
      }
      setIsModalOpen(false);
    } catch {
      toast({
        position: 'top-right',
        status: 'error',
        title: 'Something went wrong',
        description: 'The recognition value could not be saved.',
      });
    }
  };

  const handleConfirmDelete = async () => {
    if (!valuePendingDeletion) {
      return;
    }

    setIsDeleting(true);
    try {
      await onDelete(valuePendingDeletion.id);
      setValuePendingDeletion(null);
    } catch {
      toast({
        position: 'top-right',
        status: 'error',
        title: 'Something went wrong',
        description: 'The recognition value could not be deleted.',
      });
    } finally {
      setIsDeleting(false);
    }
  };

  return (
    <Flex flexDir="column" gap="16px" w="full">
      <Flex align="flex-start" justify="space-between" gap="15px">
        <Text textStyle="extraSmallRegular" color="brand.darkGray">
          {description}
        </Text>
        <Button
          leftIcon={<AddCircleOutline boxSize="18px" />}
          size="sm"
          variant="outline"
          onClick={openCreateModal}
          data-testid="add-recognition-value-button"
        >
          Add Value
        </Button>
      </Flex>

      {isLoading ? (
        <Center py="24px">
          <Spinner color="brand.purple" />
        </Center>
      ) : (
        <Flex flexDir="column" gap="8px" data-testid="recognition-values-list">
          {editableValues.length === 0 ? (
            <Text textStyle="extraSmallRegular" color="brand.darkGray">
              No values configured yet. Use “Add Value” to create one.
            </Text>
          ) : (
            editableValues.map((value) => (
              <Flex
                key={value.id}
                role="button"
                tabIndex={0}
                align="center"
                justify="space-between"
                borderWidth="1px"
                borderColor="brand.lightGray"
                borderRadius="8px"
                p="12px 16px"
                gap="12px"
                cursor="pointer"
                transition="background 0.15s ease, border-color 0.15s ease"
                _hover={{ bg: 'brand.lightPurple', borderColor: 'brand.purple' }}
                _focusVisible={{ boxShadow: 'outline', outline: 'none' }}
                onClick={() => openEditModal(value)}
                onKeyDown={(event) => {
                  if (event.key === 'Enter' || event.key === ' ') {
                    event.preventDefault();
                    openEditModal(value);
                  }
                }}
                data-testid={`recognition-value-row-${value.id}`}
              >
                <Flex align="center" gap="12px" minW="0">
                  {showEnabledToggle && onToggleEnabled && (
                    // Wrapper swallows the click the switch label re-dispatches on its input,
                    // so toggling never bubbles up to the row's edit handler.
                    <Box
                      as="span"
                      onClick={(event: React.MouseEvent<HTMLSpanElement>) =>
                        event.stopPropagation()
                      }
                    >
                      <Switch
                        isChecked={value.enabled ?? true}
                        onChange={(event) => onToggleEnabled(value, event.target.checked)}
                        aria-label={`Toggle ${value.name}`}
                        data-testid={`toggle-recognition-value-${value.id}`}
                      />
                    </Box>
                  )}
                  <Text as="span" fontSize="20px" lineHeight="1">
                    {value.emoji}
                  </Text>
                  <Text textStyle="smallMedium" color="brand.black" noOfLines={1}>
                    {value.name}
                  </Text>
                  {value.allowGifs && (
                    <Tag size="sm" variant="subtle">
                      GIFs
                    </Tag>
                  )}
                </Flex>
                <IconButton
                  aria-label={`Delete ${value.name}`}
                  icon={<DeleteOutline boxSize="16px" />}
                  variant="ghost"
                  color="text.error"
                  borderRadius="full"
                  minW="28px"
                  h="28px"
                  _hover={{ bg: 'red.50' }}
                  onClick={(event) => {
                    event.stopPropagation();
                    setValuePendingDeletion(value);
                  }}
                  data-testid={`delete-recognition-value-${value.id}`}
                />
              </Flex>
            ))
          )}
        </Flex>
      )}

      <RecognitionValueModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        editingValue={editingValue}
        availableCards={availableCards}
        showEnabledToggle={showEnabledToggle}
        isSaving={isSaving}
        onSubmit={handleSubmit}
        onUploadCard={onUploadCard}
      />

      <AlertDialog
        isOpen={valuePendingDeletion !== null}
        leastDestructiveRef={cancelDeleteRef}
        onClose={() => setValuePendingDeletion(null)}
      >
        <AlertDialogOverlay>
          <AlertDialogContent data-testid="delete-recognition-value-dialog">
            <AlertDialogHeader>Delete value</AlertDialogHeader>
            <AlertDialogBody>
              Are you sure you want to delete “{valuePendingDeletion?.name}”? This cannot be undone.
            </AlertDialogBody>
            <AlertDialogFooter>
              <Button
                ref={cancelDeleteRef}
                variant="ghost"
                onClick={() => setValuePendingDeletion(null)}
              >
                Cancel
              </Button>
              <Button
                colorScheme="red"
                ml="12px"
                onClick={handleConfirmDelete}
                isLoading={isDeleting}
                data-testid="confirm-delete-recognition-value-button"
              >
                Delete
              </Button>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialogOverlay>
      </AlertDialog>
    </Flex>
  );
}

export default RecognitionValuesManager;
